import type { ReactNode } from "react";

import { cn } from "@/lib/utils/cn";

interface CheckboxProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  /** Hide the label visually but keep it for screen readers. */
  hideLabel?: boolean;
  /**
   * Decorative content between the box and the label text — e.g. the issue's
   * thematic icon. Inside the label so clicking it still toggles.
   */
  leading?: ReactNode;
  disabled?: boolean;
  className?: string;
}

/**
 * Accessible checkbox for discrete, immediately-persisted toggles (action
 * selection, "use custom name"). Native <input type="checkbox"> so keyboard and
 * screen-reader behaviour comes for free; label association is explicit.
 */
export function Checkbox({
  checked,
  onCheckedChange,
  label,
  hideLabel = false,
  leading,
  disabled = false,
  className,
}: CheckboxProps) {
  return (
    <label
      className={cn(
        "inline-flex min-h-touch cursor-pointer items-center gap-2 text-sm",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onCheckedChange(e.target.checked)}
        className="size-4 shrink-0 rounded border-input accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      {leading}
      <span className={cn(hideLabel && "sr-only")}>{label}</span>
    </label>
  );
}
